import { useRef, type ReactNode } from 'react'

import { cn } from '@/shared/ui'

import { useHeightProperty } from './height-property'

/** The custom property holding the action bar's height, so the focused control (`index.css`) stays clear of it. */
export const actionBarHeightProperty = '--shell-action-bar-height'

/**
 * The mockups' action bar (`components/layout/StickyActionBar.jsx`): a page's end actions, such
 * as Export or Save, in a full-width band pinned to the bottom of the viewport while the end of
 * the page is below the fold. Sticky rather than the mockup's fixed, so at the end of the page it
 * settles above the legal footer instead of covering it. Place it last in `PageContainer`.
 * `info` is the text on the left, such as a count.
 */
export function StickyActionBar(props: {
  readonly info?: ReactNode
  readonly children: ReactNode
  readonly className?: string
}) {
  const barRef = useRef<HTMLDivElement>(null)
  useHeightProperty(barRef, actionBarHeightProperty)

  return (
    <div ref={barRef} className={cn('sticky bottom-0 isolate z-20 -mx-4 mt-6 sm:-mx-6', props.className)}>
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-1/2 -z-10 w-screen -translate-x-1/2 border-t border-divider bg-[color-mix(in_srgb,var(--color-background)_90%,transparent)] shadow-[0_-10px_24px_-14px_rgba(0,0,0,0.35)] backdrop-blur-md"
      />
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="text-sm text-text-secondary">{props.info}</div>
        <div className="flex flex-wrap items-center gap-2">{props.children}</div>
      </div>
    </div>
  )
}
